import React, { useCallback, useEffect, useState } from 'react';
import AlertModal from './AlertModal';
import LoaderOverlay from './LoaderOverlay';
import '../styles/formStyles.css';

const API_URL = process.env.REACT_APP_API_BASE_URL;
const WHATSAPP_SIGNATURE = '@fidelitystoreapp sua plataforma de fidelidade';

function PreClientsImport({ onGenerateRegistrationLink }) {
  const [contacts, setContacts] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [whatsappModalVisible, setWhatsappModalVisible] = useState(false);
  const [whatsappConfirmVisible, setWhatsappConfirmVisible] = useState(false);
  const [contactForWhatsapp, setContactForWhatsapp] = useState(null);
  const [whatsappMessage, setWhatsappMessage] = useState('');
  const [alert, setAlert] = useState({
    open: false,
    type: 'info',
    message: '',
  });
  const companyId = localStorage.getItem('company_id');
  const token = localStorage.getItem('token');

  const showAlert = (type, message) => {
    setAlert({ open: true, type, message });
  };

  const getPhoneDigits = (phone) => String(phone || '').replace(/\D/g, '');

  const hasPhone = (phone) => getPhoneDigits(phone).length > 0;

  const getWhatsappPhone = (phone) => {
    const digits = getPhoneDigits(phone);
    return digits.startsWith('55') ? digits : `55${digits}`;
  };

  const appendWhatsappSignature = (message) => {
    const text = String(message || '').trimEnd();
    if (!text) return WHATSAPP_SIGNATURE;
    if (text.includes(WHATSAPP_SIGNATURE)) return text;
    return `${text}\n\n${WHATSAPP_SIGNATURE}`;
  };

  const handleOpenWhatsappModal = (contact) => {
    if (!hasPhone(contact?.phone)) {
      showAlert('warning', 'Contato sem telefone informado.');
      return;
    }

    setContactForWhatsapp(contact);
    setWhatsappMessage('');
    setWhatsappConfirmVisible(false);
    setWhatsappModalVisible(true);
  };

  const handleCloseWhatsappModal = () => {
    setWhatsappModalVisible(false);
    setWhatsappConfirmVisible(false);
    setContactForWhatsapp(null);
    setWhatsappMessage('');
  };

  const handleSendWhatsappMessage = () => {
    if (!whatsappMessage.trim()) {
      showAlert('warning', 'Informe a mensagem antes de enviar.');
      return;
    }

    if (!contactForWhatsapp || !hasPhone(contactForWhatsapp.phone)) {
      showAlert('warning', 'Contato sem telefone informado.');
      return;
    }

    setWhatsappConfirmVisible(true);
  };

  const handleConfirmWhatsappSend = () => {
    const message = appendWhatsappSignature(whatsappMessage);
    const phone = getWhatsappPhone(contactForWhatsapp.phone);
    const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    handleCloseWhatsappModal();
  };

  const fetchContacts = useCallback(async () => {
    if (!companyId) {
      showAlert('error', 'Empresa do lojista não encontrada.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/companies/${companyId}/pre-clients`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao carregar os pré-clientes.');
      }

      setContacts(Array.isArray(data) ? data : []);
    } catch (error) {
      showAlert('error', error.message || 'Erro ao carregar os pré-clientes.');
    } finally {
      setLoading(false);
    }
  }, [companyId, token]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const filteredContacts = contacts.filter((contact) =>
    String(contact.name || '').toLowerCase().includes(nameFilter.trim().toLowerCase())
  );

  return (
    <section>
      <LoaderOverlay isLoading={loading} message="Carregando pré-clientes..." />
      <h3>Contatos Importados</h3>
      <div
        style={{
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
        }}
      >
        <strong>Total de pré-clientes: {filteredContacts.length}</strong>
      </div>

      <div className="filter-group" style={{ marginBottom: '20px', maxWidth: '360px' }}>
        <label htmlFor="preClientNameFilter">Nome do contato:</label>
        <input
          id="preClientNameFilter"
          type="text"
          value={nameFilter}
          onChange={(event) => setNameFilter(event.target.value)}
          placeholder="Filtrar por nome..."
          className="input-style"
        />
      </div>

      {loading ? (
        <p>Carregando pré-clientes...</p>
      ) : filteredContacts.length === 0 ? (
        <p>Nenhum pré-cliente encontrado.</p>
      ) : (
        <table className="dashboard-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Nome</th>
              <th>Telefone</th>
            </tr>
          </thead>
          <tbody>
            {filteredContacts.map((contact, index) => (
              <tr key={`${contact.phone || 'contact'}-${index}`}>
                <td>{index + 1}</td>
                <td>{contact.name || ''}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>{contact.phone || ''}</span>
                    {hasPhone(contact.phone) && (
                      <>
                        <button
                          type="button"
                          onClick={() => handleOpenWhatsappModal(contact)}
                          title="Enviar WhatsApp"
                          className="action-button"
                        >
                          <i className="bi bi-whatsapp" aria-hidden="true"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => onGenerateRegistrationLink({ nome: contact.name, telefone: contact.phone })}
                          title="Gerar link personalizado"
                          className="action-button"
                        >
                          <i className="bi bi-link-45deg" aria-hidden="true"></i>
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {whatsappModalVisible && (
        <div className="modal-overlay">
          <div className="modal-content small-modal">
            <button type="button" onClick={handleCloseWhatsappModal} className="modal-close-button">
              &times;
            </button>
            <h3>Enviar WhatsApp</h3>
            <p style={{ marginTop: 0 }}>
              Contato: <strong>{contactForWhatsapp?.name || 'N/A'}</strong>
            </p>

            <div className="label-style">
              <label htmlFor="preClientWhatsappMessage">Mensagem:</label>
              <textarea
                id="preClientWhatsappMessage"
                value={whatsappMessage}
                onChange={(event) => setWhatsappMessage(event.target.value)}
                className="input-style"
                rows={5}
                placeholder="Digite a mensagem para o contato"
              />
            </div>

            <div className="modal-actions">
              <button type="button" onClick={handleSendWhatsappMessage} className="button-style">
                Enviar
              </button>
              <button type="button" onClick={handleCloseWhatsappModal} className="button-style cancel">
                Cancelar
              </button>
            </div>
          </div>

          {whatsappConfirmVisible && (
            <div className="modal-overlay">
              <div className="modal-content small-modal">
                <h3>Confirmar envio</h3>
                <p><strong>Contato:</strong> {contactForWhatsapp?.name || 'N/A'}</p>
                <p><strong>Telefone:</strong> {contactForWhatsapp?.phone || 'N/A'}</p>
                <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                  A mensagem será enviada em nome da conta logada no computador.
                </p>
                <div className="modal-actions">
                  <button type="button" onClick={handleConfirmWhatsappSend} className="button-style">
                    Confirmar
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhatsappConfirmVisible(false)}
                    className="button-style cancel"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <AlertModal
        open={alert.open}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert((previous) => ({ ...previous, open: false }))}
      />
    </section>
  );
}

export default PreClientsImport;
